import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './InstructionPopup.css';

/**
 * A reusable instruction popup component that can be used across different pages
 * to show introductory messages with a "Don't Show This Again" option.
 */
const InstructionPopup = ({
  id,
  message,
  title = 'Instructions',
  buttonText = 'Got it!',
  onClose = () => {},
  visible = false,
}) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Update visibility when prop changes
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  // Check if this popup has been dismissed before
  useEffect(() => {
    // Check if this popup has been dismissed before
    const dismissedPopups = JSON.parse(localStorage.getItem('dismissedPopups') || '{}');
    if (!dismissedPopups[id] && !visible) {
      setIsVisible(true);
    }
  }, [id, visible]);

  const handleClose = () => {
    setIsVisible(false);

    // If "Don't Show Again" is checked, save this preference
    if (dontShowAgain) {
      // Save the user's preference
      const dismissedPopups = JSON.parse(localStorage.getItem('dismissedPopups') || '{}');
      dismissedPopups[id] = true;
      localStorage.setItem('dismissedPopups', JSON.stringify(dismissedPopups));
    }

    // Call the onClose callback
    onClose();
  };

  // Static method to reset all popups
  InstructionPopup.resetAllPopups = () => {
    localStorage.removeItem('dismissedPopups');
    // Force a reload to show all popups again
    window.location.reload();
  };

  if (!isVisible) return null;

  // Create the popup content
  const popupContent = (
    <div className="instruction-popup-overlay">
      <div className="instruction-popup">
        <h3>{title}</h3>
        <div className="instruction-content">
          {message && typeof message === 'string' ? (
            <p>{message}</p>
          ) : (
            message // In case message is a React element
          )}
          <div className="dont-show-again">
            <label>
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={e => setDontShowAgain(e.target.checked)}
              />
              Don't show this again
            </label>
          </div>
        </div>
        <button className="instruction-button" onClick={handleClose}>{buttonText}</button>
      </div>
    </div>
  );

  // Render the popup using a portal
  return createPortal(
    popupContent,
    document.body
  );
};

InstructionPopup.propTypes = {
  id: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired,
  title: PropTypes.string,
  buttonText: PropTypes.string,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
};

export default InstructionPopup;
